// Phase-3 Slice 28 — Rename batch executor.
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 28.
//
// Consumes the `RenameBatch` produced by Slice 27's planner and runs the
// schema-side ALTER + per-object body ALTERs against the live connection.
// Per-dialect transaction discipline:
//
//   - mssql / pgsql / sqlite — run the whole ALTER sequence atomically on a
//     single driver-native pinned session via `executeBatch`
//     (`transaction:execute-batch` → `executeBatchOnNewSession`); the
//     session begins/commits/rolls back natively (no BEGIN/COMMIT/ROLLBACK
//     SQL strings). Any statement failure rolls the whole batch back.
//   - mysql / oracle         — DDL is auto-commit; execute sequentially and
//     fall back to running the planner's `undoBatch` (in reverse order of
//     successful steps) when a step fails.
//
// Pre-flight (two distinct refusals, neither executes any SQL):
//   1. NAV-2 unrewritable-rename (`isUnrewritableRenameSql`) — the emitter
//      landed but declined to avoid data loss; the embedded reason is
//      surfaced to the user verbatim.
//   2. Defensive stub guard (`isStubAlterSql`) — a not-yet-filled placeholder
//      ALTER reached apply; all per-dialect emitters have landed (Slices
//      29–33 complete), so this is a should-not-happen internal-invariant
//      check. Reported with the generic defensive stub diagnostic.

import type { DatabaseEngineId } from '@/core/engines';
import type { QueryExecutionResult } from '@/types/queryExecution';

import type {
  AlterStatement,
  AlterStatementResult,
  RenameBatch,
  RenameResult,
} from '../types';

import {
  runIndexRefreshForRename,
  type RenameRefreshedObject,
} from '../index/indexInvalidator';

import {
  isStubAlterSql,
  isUnrewritableRenameSql,
  unrewritableRenameReason,
} from './emit';

export type ExecuteSqlInput = {
  readonly connectionId: string;
  readonly database: string;
  readonly sql: string;
};

export type ExecuteBatchInput = {
  readonly connectionId: string;
  readonly database: string;
  readonly engineType: DatabaseEngineId;
  readonly statements: readonly string[];
};

export type ExecuteBatchResult = {
  readonly status: 'committed' | 'rolled-back';
  readonly failedIndex?: number;
  readonly error?: string;
};

export type ExecuteRenameDeps = {
  readonly executeSql?: (input: ExecuteSqlInput) => Promise<QueryExecutionResult>;
  readonly executeBatch?: (input: ExecuteBatchInput) => Promise<ExecuteBatchResult>;
};

export type ExecuteRenameInput = {
  readonly connectionId: string;
  readonly database: string;
  readonly batch: RenameBatch;
};

const STUB_ERROR =
  'Cannot apply rename: an internal invariant was violated — a rename ALTER was still an unfilled placeholder when apply was attempted. All per-dialect emitters have landed, so this should not occur; please report this as a bug.';

// NAV-2: the body-unavailable refusal is NOT a missing emitter — the emitter
// landed and deliberately refused to avoid losing the view definition. Surface
// the embedded reason instead of the misleading STUB_ERROR.
function unrewritableRenameError(reason: string): string {
  return `Rename refused to prevent data loss: ${reason}`;
}

const UNREWRITABLE_ERROR =
  'Cannot apply rename: the planner could not rewrite every dependent object ' +
  '(see "Cannot rewrite"). Applying the schema rename would leave those ' +
  'references broken, so the batch was refused.';

const TRANSACTIONAL_ENGINES: ReadonlySet<DatabaseEngineId> = new Set([
  'mssql',
  'pgsql',
  'sqlite',
] satisfies DatabaseEngineId[]);

export async function executeRename(
  input: ExecuteRenameInput,
  deps?: ExecuteRenameDeps,
): Promise<RenameResult> {
  const { connectionId, database, batch } = input;
  const exec = deps?.executeSql ?? defaultExecuteSql;
  const batchExec = deps?.executeBatch ?? defaultExecuteBatch;

  const sequence: readonly AlterStatement[] = [
    batch.schemaAlter,
    ...batch.objectEdits.map((edit) => edit.alter),
  ];

  // NAV-2: a typed unrewritable-rename refusal (emitter landed but declined to
  // avoid data loss — e.g. a MySQL view rename with no original body). This
  // shares the stub marker prefix so the batch is still refused, but the real
  // reason is embedded and must reach the user — NOT the generic defensive
  // stub diagnostic.
  const unrewritable = sequence.find((alter) =>
    isUnrewritableRenameSql(alter.sql),
  );
  if (unrewritable) {
    const reason =
      unrewritableRenameReason(unrewritable.sql) ?? 'the rename could not be rewritten safely';
    return {
      status: 'refused',
      alters: sequence.map((alter) => ({
        alter,
        status: 'failure',
        error: 'Refused — rename would lose data; not executed.',
      })),
      errorMessage: unrewritableRenameError(reason),
    };
  }

  const stub = sequence.find((alter) => isStubAlterSql(alter.sql));
  if (stub) {
    return {
      status: 'refused',
      alters: sequence.map((alter) => ({
        alter,
        status: 'failure',
        error: 'Refused — unfilled placeholder ALTER (internal invariant; should not occur).',
      })),
      errorMessage: STUB_ERROR,
    };
  }

  // Safety net: a non-empty unrewritable[] means at least one dependent body
  // (parse-failed / body-missing / no-emitter / unsupported-op — e.g. a stored
  // proc, trigger, or SQLite constraint) would keep referencing the old name.
  // Running the schema-side ALTER anyway
  // silently breaks those objects, so refuse the whole batch.
  if (batch.unrewritable.length > 0) {
    return {
      status: 'refused',
      alters: sequence.map((alter) => ({
        alter,
        status: 'failure',
        error: 'Refused — some dependent objects could not be rewritten.',
      })),
      errorMessage: UNREWRITABLE_ERROR,
    };
  }

  const result = TRANSACTIONAL_ENGINES.has(batch.dialect)
    ? await runTransactional(batchExec, connectionId, database, batch.dialect, sequence)
    : await runNonTransactional(exec, connectionId, database, batch, sequence);

  // NAV-3: the ALTERs above ran directly through `executeQueryV2`, bypassing
  // the `useQueryExecution.ts` → `runIndexInvalidationForDdl` hook, so the
  // in-memory parsed-object index would otherwise keep the OLD identifier +
  // OLD bodies until reconnect. Refresh it here for any apply that landed at
  // least one ALTER (`success` or non-transactional `partial-failure`).
  // `rolled-back` / `refused` changed nothing, so the index stays as-is.
  if (result.status === 'success' || result.status === 'partial-failure') {
    await runIndexRefreshForRename(connectionId, {
      target: {
        kind: batch.target.kind,
        fromIdentifier: batch.target.fromIdentifier,
        toIdentifier: batch.target.toIdentifier,
      },
      appliedBodyObjects: appliedBodyObjects(batch, result),
    }).catch(() => {});
  }

  return result;
}

/** Map every successfully-applied body-rewrite ALTER back to its
 *  `ObjectBodyEdit` (matched by `alter` reference identity — the same object
 *  flows from `batch.objectEdits[i].alter` into `sequence` into
 *  `result.alters[i].alter`). Only successfully-applied objects are returned
 *  so a partial failure never refreshes an object whose ALTER never landed. */
function appliedBodyObjects(
  batch: RenameBatch,
  result: RenameResult,
): readonly RenameRefreshedObject[] {
  const appliedAlters = new Set(
    result.alters
      .filter((a) => a.status === 'success' && a.alter.purpose === 'object-body-rewrite')
      .map((a) => a.alter),
  );
  return batch.objectEdits
    .filter((edit) => appliedAlters.has(edit.alter))
    .map((edit) => ({ objectKind: edit.objectKind, objectName: edit.objectName }));
}

async function runTransactional(
  batchExec: (input: ExecuteBatchInput) => Promise<ExecuteBatchResult>,
  connectionId: string,
  database: string,
  engine: DatabaseEngineId,
  sequence: readonly AlterStatement[],
): Promise<RenameResult> {
  const batch = await batchExec({
    connectionId,
    database,
    engineType: engine,
    statements: sequence.map((a) => a.sql),
  });

  if (batch.status === 'committed') {
    return {
      status: 'success',
      alters: sequence.map((alter) => ({ alter, status: 'success' as const })),
    };
  }

  // rolled-back: failedIndex identifies which statement failed (or -1 for begin/acquire failure)
  const failedIndex = batch.failedIndex ?? -1;
  const alters: AlterStatementResult[] = [];

  if (failedIndex >= 0) {
    for (let i = 0; i < failedIndex; i++) {
      alters.push({ alter: sequence[i], status: 'success' });
    }
    alters.push({ alter: sequence[failedIndex], status: 'failure', error: batch.error });
  }

  return {
    status: 'rolled-back',
    alters,
    errorMessage: batch.error,
  };
}

async function runNonTransactional(
  exec: (input: ExecuteSqlInput) => Promise<QueryExecutionResult>,
  connectionId: string,
  database: string,
  batch: RenameBatch,
  sequence: readonly AlterStatement[],
): Promise<RenameResult> {
  const alters: AlterStatementResult[] = [];
  let failureIndex = -1;
  let failureMessage: string | undefined;

  for (let i = 0; i < sequence.length; i++) {
    const alter = sequence[i];
    const res = await exec({ connectionId, database, sql: alter.sql });
    if (res.success) {
      alters.push({ alter, status: 'success' });
      continue;
    }
    failureMessage = errorMessage(res);
    alters.push({ alter, status: 'failure', error: failureMessage });
    failureIndex = i;
    break;
  }

  if (failureIndex < 0) {
    return { status: 'success', alters };
  }

  // Walk the undoBatch (parallel to forward sequence) in reverse for the
  // successful-only prefix to revert what auto-committed.
  const undoExecuted: AlterStatementResult[] = [];
  for (let i = failureIndex - 1; i >= 0; i--) {
    const undoAlter = batch.undoBatch[i];
    if (!undoAlter) continue;
    const res = await exec({ connectionId, database, sql: undoAlter.sql });
    undoExecuted.push({
      alter: undoAlter,
      status: res.success ? 'success' : 'failure',
      error: res.success ? undefined : errorMessage(res),
    });
  }

  return {
    status: 'partial-failure',
    alters,
    undoExecuted,
    errorMessage: failureMessage,
  };
}

function errorMessage(result: QueryExecutionResult): string {
  return result.error?.message ?? 'unknown error';
}

async function defaultExecuteSql(input: ExecuteSqlInput): Promise<QueryExecutionResult> {
  if (typeof window === 'undefined' || !window.electronAPI?.executeQueryV2) {
    return {
      success: false,
      error: { message: 'executeQueryV2 is not available' },
    };
  }
  return window.electronAPI.executeQueryV2({
    connectionId: input.connectionId,
    database: input.database,
    sql: input.sql,
    executionId: nextExecutionId(input.connectionId),
  });
}

async function defaultExecuteBatch(input: ExecuteBatchInput): Promise<ExecuteBatchResult> {
  if (typeof window === 'undefined' || !window.electronAPI?.transaction?.executeBatch) {
    return { status: 'rolled-back', error: 'transaction.executeBatch is not available' };
  }
  return window.electronAPI.transaction.executeBatch({
    connectionId: input.connectionId,
    database: input.database,
    // Rename never targets kusto or azurestorage — `getRenameEmitter` returns
    // null for both, so this path is unreachable for the two engines that
    // `executeBatch`'s transaction-capable `TransactionEngineType` excludes.
    engineType: input.engineType as Exclude<DatabaseEngineId, 'kusto' | 'azurestorage'>,
    statements: input.statements as string[],
  });
}

function nextExecutionId(connectionId: string): string {
  return `nav-rename-apply-${connectionId}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
